import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";

const emptyChoice = () => ({ action: "", targetCategoryId: "", targetSubcategoryId: "" });

function unwrapImpact(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (Array.isArray(raw.groups) || raw.category) return raw;
  const inner = raw.data;
  if (inner && typeof inner === "object" && (Array.isArray(inner.groups) || inner.category)) {
    return inner;
  }
  return null;
}

function choiceReady(actions, choice) {
  if (!choice?.action) return false;
  const action = (actions || []).find((item) => item.id === choice.action);
  if (!action) return false;
  return !action.needsTarget || Boolean(choice.targetCategoryId) || Boolean(choice.targetSubcategoryId);
}

function DecisionControls({ idPrefix, actions, choice, targets, onChange }) {
  const { t } = useTranslation("settings");
  const action = (actions || []).find((item) => item.id === choice.action);
  const selectedCat =
    (targets || []).find((row) => String(row.id) === String(choice.targetCategoryId)) || null;
  const subcats = selectedCat?.children || [];
  const selectedSub =
    subcats.find((row) => String(row.id) === String(choice.targetSubcategoryId)) || null;

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <FormControl size="small" fullWidth>
        <InputLabel id={`${idPrefix}-action`}>{t("categoriesPage.deleteDialog.actionLabel")}</InputLabel>
        <Select
          labelId={`${idPrefix}-action`}
          label={t("categoriesPage.deleteDialog.actionLabel")}
          value={choice.action}
          onChange={(event) =>
            onChange({ action: event.target.value, targetCategoryId: "", targetSubcategoryId: "" })
          }
        >
          {(actions || []).map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {action?.needsTarget ? (
        <>
          <Autocomplete
            size="small"
            fullWidth
            options={targets || []}
            value={selectedCat}
            getOptionLabel={(option) => option?.nome || ""}
            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            onChange={(_event, value) =>
              onChange({
                targetCategoryId: value?.id ? String(value.id) : "",
                targetSubcategoryId: "",
              })
            }
            renderInput={(params) => (
              <TextField {...params} label={t("categoriesPage.deleteDialog.targetCategory")} />
            )}
          />
          <Autocomplete
            size="small"
            fullWidth
            options={subcats}
            value={selectedSub}
            disabled={!selectedCat}
            getOptionLabel={(option) => option?.nome || ""}
            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            onChange={(_event, value) =>
              onChange({ targetSubcategoryId: value?.id ? String(value.id) : "" })
            }
            renderInput={(params) => (
              <TextField {...params} label={t("categoriesPage.deleteDialog.targetSubcategory")} />
            )}
          />
        </>
      ) : null}
    </Stack>
  );
}

function GroupCard({ group, state, targets, onChange }) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const tone = theme.palette.primary.main;
  const items = group.items || [];
  const selectedIds = Object.keys(state.selected || {}).filter((id) => state.selected[id]);
  const defined = items.filter((item) => choiceReady(group.actions, state.items?.[String(item.id)])).length;
  const allChecked = items.length > 0 && selectedIds.length === items.length;

  const applyTo = (ids) => {
    if (!choiceReady(group.actions, state.draft)) return;
    const nextItems = { ...(state.items || {}) };
    ids.forEach((id) => {
      nextItems[String(id)] = { ...state.draft };
    });
    onChange({
      ...state,
      items: nextItems,
      selected: {},
      coverRemainder: ids.length === items.length ? { ...state.draft } : state.coverRemainder,
    });
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: alpha(tone, 0.35),
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(tone, 0.04),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ px: 2, py: 1.5, bgcolor: alpha(tone, 0.1) }}>
        <Avatar sx={{ bgcolor: alpha(tone, 0.16), color: tone, width: 36, height: 36 }}>
          <Inventory2OutlinedIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {group.label}
            </Typography>
            <Chip size="small" label={group.total} sx={{ fontWeight: 700, bgcolor: alpha(tone, 0.16), color: tone }} />
            <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary" }}>
              {t("categoriesPage.deleteDialog.definedShort", { defined, listed: items.length })}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {group.hint}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ px: 2, py: 1.5, bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3, color: "text.secondary" }}>
          {t("categoriesPage.deleteDialog.applyBatch")}
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ mt: 1 }}>
          <DecisionControls
            idPrefix={`${group.key}-draft`}
            actions={group.actions}
            choice={state.draft || emptyChoice()}
            targets={targets}
            onChange={(patch) => onChange({ ...state, draft: { ...(state.draft || emptyChoice()), ...patch } })}
          />
          <Button
            size="small"
            variant="contained"
            disabled={!choiceReady(group.actions, state.draft)}
            onClick={() => applyTo(items.map((item) => item.id))}
          >
            {t("categoriesPage.deleteDialog.applyAll")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedIds.length || !choiceReady(group.actions, state.draft)}
            onClick={() => applyTo(selectedIds)}
          >
            {t("categoriesPage.deleteDialog.applySelected", { count: selectedIds.length })}
          </Button>
        </Stack>
      </Box>

      <Stack sx={{ maxHeight: 320, overflow: "auto", bgcolor: "background.paper" }}>
        {items.map((item) => {
          const id = String(item.id);
          const itemChoice = state.items?.[id] || emptyChoice();
          const ready = choiceReady(group.actions, itemChoice);
          const action = (group.actions || []).find((entry) => entry.id === itemChoice.action);
          return (
            <Stack
              key={id}
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ md: "center" }}
              sx={{
                px: 1.5,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: ready ? alpha(theme.palette.success.main, 0.04) : "background.paper",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Checkbox
                  size="small"
                  checked={Boolean(state.selected?.[id])}
                  onChange={(event) =>
                    onChange({
                      ...state,
                      selected: { ...(state.selected || {}), [id]: event.target.checked },
                    })
                  }
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={item.label}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{item.id}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={ready ? action?.label : t("categoriesPage.deleteDialog.pending")}
                  color={ready ? "success" : "default"}
                  variant={ready ? "filled" : "outlined"}
                />
              </Stack>
              <DecisionControls
                idPrefix={`${group.key}-${id}`}
                actions={group.actions}
                choice={itemChoice}
                targets={targets}
                onChange={(patch) =>
                  onChange({
                    ...state,
                    items: { ...(state.items || {}), [id]: { ...itemChoice, ...patch } },
                  })
                }
              />
            </Stack>
          );
        })}
        {group.total > items.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, fontStyle: "italic" }}>
            {t("categoriesPage.deleteDialog.remainder", { count: group.total - items.length })}
          </Typography>
        ) : null}
      </Stack>

      {items.length > 0 ? (
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 1, py: 0.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
        >
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={selectedIds.length > 0 && !allChecked}
            onChange={(event) => {
              const selected = {};
              if (event.target.checked) {
                items.forEach((item) => {
                  selected[String(item.id)] = true;
                });
              }
              onChange({ ...state, selected });
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {t("categoriesPage.deleteDialog.selectAll")}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
}

export default function CategoryDeletionDialog({
  open,
  category,
  submitting = false,
  errorMessage = "",
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [impact, setImpact] = useState(null);
  const [groupsState, setGroupsState] = useState({});

  useEffect(() => {
    if (!open || !category?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setImpact(null);
    setGroupsState({});
    fetchData(`/categories/${category.id}/deletion-impact`)
      .then((data) => {
        if (cancelled) return;
        const impactPayload = unwrapImpact(data);
        if (!impactPayload) {
          setLoadError(t("categoriesPage.deleteDialog.loadError"));
          setImpact(null);
          return;
        }
        setImpact(impactPayload);
        const next = {};
        for (const group of impactPayload.groups || []) {
          next[group.key] = { draft: emptyChoice(), selected: {}, items: {}, coverRemainder: null };
        }
        setGroupsState(next);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || t("categoriesPage.deleteDialog.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, category?.id, t]);

  const targets = useMemo(() => impact?.targets || [], [impact]);
  const kind = impact?.category?.kind || (Number(category?.id_pai) > 0 ? "subcategory" : "category");
  const displayName = impact?.category?.nome || category?.nome || "";

  const pending = (impact?.groups || []).some((group) => {
    const state = groupsState[group.key] || {};
    const items = group.items || [];
    const missingItem = items.some((item) => !choiceReady(group.actions, state.items?.[String(item.id)]));
    const remainder = group.total > items.length && !choiceReady(group.actions, state.coverRemainder);
    return missingItem || remainder;
  });

  const definedCount = (impact?.groups || []).reduce((sum, group) => {
    const state = groupsState[group.key] || {};
    return sum + (group.items || []).filter((item) => choiceReady(group.actions, state.items?.[String(item.id)])).length;
  }, 0);
  const listedCount = (impact?.groups || []).reduce((sum, group) => sum + (group.items || []).length, 0);

  const toResolutions = () => {
    const resolutions = {};
    for (const group of impact?.groups || []) {
      const state = groupsState[group.key] || {};
      const items = {};
      for (const item of group.items || []) {
        const choice = state.items?.[String(item.id)];
        if (!choice?.action) continue;
        items[String(item.id)] = {
          action: choice.action,
          targetCategoryId: choice.targetCategoryId ? Number(choice.targetCategoryId) : null,
          targetSubcategoryId: choice.targetSubcategoryId ? Number(choice.targetSubcategoryId) : null,
        };
      }
      const cover = state.coverRemainder;
      resolutions[group.key] = {
        action: cover?.action || "",
        targetCategoryId: cover?.targetCategoryId ? Number(cover.targetCategoryId) : null,
        targetSubcategoryId: cover?.targetSubcategoryId ? Number(cover.targetSubcategoryId) : null,
        items,
      };
    }
    return resolutions;
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose?.()} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.12), color: "error.main" }}>
            <CategoryOutlinedIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {kind === "subcategory"
                ? t("categoriesPage.deleteDialog.titleSubcategory")
                : t("categoriesPage.deleteDialog.titleCategory")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayName
                ? t("categoriesPage.deleteDialog.subtitle", { name: displayName })
                : t("categoriesPage.deleteDialog.subtitleFallback")}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "grey.50", px: { xs: 1.5, sm: 2.5 } }}>
        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">{t("categoriesPage.deleteDialog.loading")}</Typography>
          </Stack>
        )}

        {!loading && (
          <Stack spacing={2} sx={{ py: 0.5 }}>
            {(loadError || errorMessage) && <Alert severity="error">{errorMessage || loadError}</Alert>}

            {(impact?.groups || []).length === 0 && !loadError && (
              <Alert severity="success">{t("categoriesPage.deleteDialog.noProducts")}</Alert>
            )}

            {listedCount > 0 && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("categoriesPage.deleteDialog.definedCount", { defined: definedCount, listed: listedCount })}
              </Typography>
            )}

            {(impact?.groups || []).map((group) => (
              <GroupCard
                key={group.key}
                group={group}
                targets={targets}
                state={groupsState[group.key] || { draft: emptyChoice(), selected: {}, items: {} }}
                onChange={(next) => setGroupsState((prev) => ({ ...prev, [group.key]: next }))}
              />
            ))}

            {(impact?.automatic || []).length > 0 && (
              <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 2, p: 2, bgcolor: "background.paper" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <InfoOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t("categoriesPage.deleteDialog.automatic")}
                  </Typography>
                </Stack>
                {(impact.automatic || []).map((note) => (
                  <Typography key={note} variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 0.5 }}>
                    {note}
                  </Typography>
                ))}
                {(impact.children || []).length > 0 && (
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                    {impact.children.map((child) => (
                      <Chip key={child.id} size="small" variant="outlined" label={child.nome} />
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t("shared.cancel")}
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={loading || submitting || Boolean(loadError) || pending}
          onClick={() => onConfirm?.(toResolutions())}
        >
          {submitting ? t("categoriesPage.deleteDialog.confirming") : t("categoriesPage.deleteDialog.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
